// react
import { useEffect, useRef } from "react";

interface BaseProps {
	checkForUnsavedData: () => boolean;
	message?: string;
}

/**
 * BeforeUnload component accepts an checkForUnsavedData function that will be referenced
 * when the user tries to leave the page.
 *
 * checkedForUnsavedData is a function that will return a boolean.
 *
 * If checkedForUnsavedData returns false, the function returns an empty string, and does nothing
 * else special,
 *
 * if checkedForUnsavedData returns true, the function handles the meeting various different
 * browser requirements for triggering it's native behavior to prevent losing form data.
 *
 * This "component" creates the onBeforeUnload function and attaches
 * it to the window's beforeunload event via the useEffect hook.
 */
const BeforeUnload = (props: BaseProps) => {
	const { checkForUnsavedData, message = "You have unsaved form data." } = props;

	const onBeforeUnload = (event: BeforeUnloadEvent) => {
		const e = event || window.event;
		const hasUnsaved = checkForUnsavedData();
		if (!hasUnsaved) {
			// we assume an empty form contains no unsaved data
			return "";
		}
		// if the form is filled in we handle it first by preventing default
		if (e.preventDefault) e.preventDefault();
		// chrome requires event.returnValue to be set
		e.returnValue = message;

		// some browsers expect a message returned
		return message;
	};
	const beforeUnloadRef = useRef<(event: BeforeUnloadEvent) => string>(onBeforeUnload);

	useEffect(() => {
		beforeUnloadRef.current = onBeforeUnload;
	}, [onBeforeUnload]);

	useEffect(() => {
		const beforeUnload = (args: BeforeUnloadEvent) => beforeUnloadRef.current(args);
		window.addEventListener("beforeunload", beforeUnload);

		return () => window.removeEventListener("beforeunload", beforeUnload);
	}, []);

	// react will complain if nothing is returned, however null is allowed since react 15
	// https://github.com/facebook/react/pull/5884
	return null;
};

export default BeforeUnload;
